import { Button } from "@/components/core/buttons/Button";
import { cn, modalWidthClass } from "@/lib/utils";
import { Text } from "components/core/Text";
import { Tooltip } from "components/core/Tooltip";
import { AppContext } from "contexts/AppContext";
import {
    Mix_Content_Tag_Enum_Enum,
    useSessionQuery,
    useUpdateMeMutation,
    useUpsertUserInterestTagsMutation
} from "graphql/generated";
import { useAuth } from "hooks/useAuth";
import { fireEvent } from "lib/ga";
import Image from "next/image";
import { useRouter } from "next/router";
import {
    ComponentProps,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from "react";
import {
    EXTENSION_STORE_URLS,
    MIX_CONTENT_TAG_ENUM_KOREAN,
    ORDERED_CAREERS,
    ORDERED_MIX_CONTENT_TAG_ENUM,
    ORDERED_POSITION,
    POSITION_KOREAN,
    Position,
    SHINHAN_INVEST_TAG_LIST
} from "utils/enum";

import { Divider } from "../Divider";
import { Checkbox } from "../inputs/CheckBox";
import { MixModal } from "./MixModal";

enum Stage {
    position,
    career,
    subscribeEmail,
    installExtension,
    selectInterestTag
}

type SelectType<T = any> = (data: { [key in Stage]: T }) => T;

export const CareerModal = ({
    me,
    close,
    onRequestClose,
    ...props
}: ComponentProps<typeof MixModal> & {
    me: NonNullable<
        NonNullable<ReturnType<typeof useSessionQuery>["data"]>["session"]["me"]
    >;
    close: () => Promise<void>;
}) => {
    const router = useRouter();
    const { refetch } = useAuth();
    const { browserName, deviceType } = useContext(AppContext);
    const [upsertUserInterestTags] = useUpsertUserInterestTagsMutation();

    const defaultTags = useMemo(
        () =>
            ORDERED_MIX_CONTENT_TAG_ENUM.filter(
                (tag) => !(SHINHAN_INVEST_TAG_LIST as string[]).includes(tag)
            ),
        []
    );

    const [stage, setStage] = useState<Stage>(Stage.position);
    const [position, setPosition] = useState<Position | null>(null);
    const [career, setCareer] = useState<string | null>(null);
    const [subscribeEmail, setSubscribeEmail] = useState<boolean>(
        me?.subscribeEmail ?? false
    );
    const [selectedTags, setSelectedTags] = useState(
        new Set<Mix_Content_Tag_Enum_Enum>()
    );
    const [isFirstToggle, setIsFirstToggle] = useState(true);
    const [showTooltip, setShowTooltip] = useState(false);

    // Browser detection for extension install modal
    const getExtensionUrl = useCallback(() => {
        const browserKey = browserName.toUpperCase();
        return (
            EXTENSION_STORE_URLS[browserKey]?.url ||
            EXTENSION_STORE_URLS.CHROME?.url ||
            ""
        );
    }, [browserName]);

    const shouldShowExtensionModal = useMemo(() => {
        const isChromiumBased =
            typeof navigator !== "undefined" &&
            !!navigator.userAgentData &&
            navigator.userAgentData.brands.some(
                (data) => data.brand === "Chromium"
            );

        return (
            isChromiumBased &&
            deviceType === "desktop" &&
            EXTENSION_STORE_URLS[browserName.toUpperCase()]
        );
    }, [browserName, deviceType]);

    // 초기에 모든 태그를 선택된 상태로 설정
    useEffect(() => {
        const allTags = new Set([
            ...defaultTags.map((tag) => tag as Mix_Content_Tag_Enum_Enum),
            ...(me.group_id === "shinhaninvest"
                ? (SHINHAN_INVEST_TAG_LIST as Mix_Content_Tag_Enum_Enum[])
                : [])
        ]);
        setSelectedTags(allTags);
    }, [defaultTags, me.group_id]);

    const isNoTagsSelected = selectedTags.size === 0 && !isFirstToggle;

    // 태그가 선택되면 tooltip 숨기기
    useEffect(() => {
        if (!isNoTagsSelected) {
            setShowTooltip(false);
        }
    }, [isNoTagsSelected]);

    const toggleTag = async (tag: Mix_Content_Tag_Enum_Enum) => {
        if (isFirstToggle) {
            // 첫 토글 시: 현재 선택된 태그들(OFF한 태그 제외)만 upsert
            setIsFirstToggle(false);

            // 로컬 상태 먼저 업데이트 (OFF한 태그 제거)
            const updatedTags = new Set(selectedTags);
            updatedTags.delete(tag);
            setSelectedTags(updatedTags);

            try {
                // 업데이트된 태그들만 upsert
                if (updatedTags.size > 0) {
                    const tagObjects = Array.from(updatedTags).map(
                        (tagValue, index) => ({
                            user_id: me.id,
                            tag_value: tagValue as Mix_Content_Tag_Enum_Enum,
                            priority: index + 1
                        })
                    );

                    await upsertUserInterestTags({
                        variables: {
                            objects: tagObjects
                        }
                    });
                }
            } catch (error) {
                setIsFirstToggle(true); // 실패 시 되돌리기
                // 실패 시 로컬 상태도 되돌리기
                setSelectedTags(selectedTags);
            }
        } else {
            // 일반 토글 로직
            setSelectedTags((prev) => {
                const newSet = new Set(prev);
                if (newSet.has(tag)) {
                    newSet.delete(tag);
                } else {
                    newSet.add(tag);
                }
                return newSet;
            });
        }
    };

    const toggleAllTags = async (tagList: readonly string[]) => {
        const allSelected = tagList.every((tag) =>
            selectedTags.has(tag as Mix_Content_Tag_Enum_Enum)
        );

        if (isFirstToggle) {
            // 첫 토글 시: 전체 선택/해제 처리
            setIsFirstToggle(false);

            if (allSelected) {
                // 전체 해제: 해당 태그 그룹을 모두 제거
                const updatedTags = new Set(selectedTags);
                tagList.forEach((tag) => {
                    updatedTags.delete(tag as Mix_Content_Tag_Enum_Enum);
                });
                setSelectedTags(updatedTags);

                try {
                    // 업데이트된 태그들만 upsert
                    if (updatedTags.size > 0) {
                        const tagObjects = Array.from(updatedTags).map(
                            (tagValue, index) => ({
                                user_id: me.id,
                                tag_value:
                                    tagValue as Mix_Content_Tag_Enum_Enum,
                                priority: index + 1
                            })
                        );

                        await upsertUserInterestTags({
                            variables: {
                                objects: tagObjects
                            }
                        });
                    }
                } catch (error) {
                    setIsFirstToggle(true);
                    setSelectedTags(selectedTags); // 실패 시 되돌리기
                }
            } else {
                // 전체 선택: 해당 태그 그룹을 모두 추가
                const updatedTags = new Set(selectedTags);
                tagList.forEach((tag) => {
                    updatedTags.add(tag as Mix_Content_Tag_Enum_Enum);
                });
                setSelectedTags(updatedTags);

                try {
                    // 업데이트된 태그들만 upsert
                    const tagObjects = Array.from(updatedTags).map(
                        (tagValue, index) => ({
                            user_id: me.id,
                            tag_value: tagValue as Mix_Content_Tag_Enum_Enum,
                            priority: index + 1
                        })
                    );

                    await upsertUserInterestTags({
                        variables: {
                            objects: tagObjects
                        }
                    });
                } catch (error) {
                    setIsFirstToggle(true);
                    setSelectedTags(selectedTags); // 실패 시 되돌리기
                }
            }
        } else {
            // 일반 토글 로직
            if (allSelected) {
                // 전체 해제: 선택된 태그들을 한 번에 해제
                setSelectedTags((prev) => {
                    const newSet = new Set(prev);
                    tagList.forEach((tag) => {
                        if (newSet.has(tag as Mix_Content_Tag_Enum_Enum)) {
                            newSet.delete(tag as Mix_Content_Tag_Enum_Enum);
                        }
                    });
                    return newSet;
                });
            } else {
                // 전체 선택: 선택되지 않은 태그들을 한 번에 선택
                setSelectedTags((prev) => {
                    const newSet = new Set(prev);
                    tagList.forEach((tag) => {
                        if (!newSet.has(tag as Mix_Content_Tag_Enum_Enum)) {
                            newSet.add(tag as Mix_Content_Tag_Enum_Enum);
                        }
                    });
                    return newSet;
                });
            }
        }
    };

    const isAllTagsSelected = (tagList: readonly string[]) => {
        return (
            tagList.length > 0 &&
            tagList.every((tag) =>
                selectedTags.has(tag as Mix_Content_Tag_Enum_Enum)
            )
        );
    };

    const select = useCallback<SelectType>((data) => data[stage], [stage]);

    const [updateMe] = useUpdateMeMutation({
        onCompleted: async (response) => {
            if (response.update_mix_user_by_pk) {
                const { career, position } = response.update_mix_user_by_pk;
                const prev_webview_access =
                    router.query.prev_webview_access === "true";

                // window.fbq("track", "CompleteRegistration", {
                //   method: me?.method,
                // });

                fireEvent({
                    event: "sign_up",
                    params: {
                        method: me?.method,
                        subscribeEmail,
                        prev_webview_access
                    }
                });

                fireEvent({
                    params: {
                        career,
                        position
                    }
                });
            }

            await close();
            router.reload(); // clear gtm static params
        }
    });

    useEffect(() => {
        refetch();
    }, [refetch]);

    return (
        <MixModal {...props}>
            <div
                className={cn(
                    `relative mx-6 flex flex-col items-center justify-center
                    rounded-[24px] bg-common-white-main px-4 py-14
                    dark:bg-grey-100 [&_*]:text-center`,
                    "md:rounded-[20px] md:px-8 md:py-12 md:pb-8",
                    modalWidthClass,
                    stage === Stage.subscribeEmail && "max-w-[342px] p-4 py-10",
                    stage === Stage.installExtension &&
                        "max-w-[342px] p-4 py-[33px]"
                )}
            >
                <Text
                    className={cn(
                        "mb-10 whitespace-pre-wrap",
                        `md:mb-8 md:text-2xl md:font-bold
                        md:leading-[135.0000023841858%]`,
                        (stage === Stage.subscribeEmail ||
                            stage === Stage.installExtension) &&
                            "mb-2"
                    )}
                    variant="h2"
                    tag="header"
                >
                    {select({
                        [Stage.position]: "나의 직무를 선택해주세요.",
                        [Stage.career]: "나의 연차를 선택해주세요.",
                        [Stage.selectInterestTag]: (
                            <div className="flex flex-col items-center gap-1">
                                <div>관심 주제를 선택해 주세요.</div>
                                <Text
                                    variant="body1"
                                    color="text.secondary"
                                    className="md:text-sm md:font-normal
                                        md:leading-[139.9999976158142%]"
                                >
                                    이후에도 언제든 변경할 수 있어요.
                                </Text>
                            </div>
                        ),
                        [Stage.subscribeEmail]: (
                            <div className="flex flex-col items-center">
                                <div className="mb-2 py-[13px]">
                                    <div className="relative h-[74px] w-[74px]">
                                        <Image
                                            alt="newsletter"
                                            src="/img/newsletter.png"
                                            fill={true}
                                        />
                                    </div>
                                </div>
                                {`믹스 뉴스레터를\n받아 보시겠어요?`}
                            </div>
                        ),
                        [Stage.installExtension]: (
                            <div
                                className="-m-[0.1rem] flex flex-col
                                    items-center"
                            >
                                <div
                                    className="mb-3 flex h-[89px] w-[99px]
                                        items-center justify-center py-[13px]
                                        text-[73px] text-primary-main"
                                >
                                    🔥
                                </div>
                                <Text variant="h4" color="text.primary">
                                    확장 프로그램 사용자는 매주
                                    <br />
                                    <span
                                        className="font-bold text-primary-main"
                                    >
                                        평균 13개의 콘텐츠
                                    </span>
                                    를 더 읽고 있어요!
                                </Text>
                            </div>
                        )
                    })}
                </Text>
                <div className="flex w-full flex-col items-center">
                    {select({
                        [Stage.position]: (
                            <div
                                className="flex w-full flex-col gap-2 md:grid
                                    md:grid-cols-[repeat(3,minmax(0,1fr))]
                                    md:gap-1.5"
                            >
                                {ORDERED_POSITION.map(($position) => (
                                    <button
                                        key={$position}
                                        className={cn(
                                            `h-[54px] w-full break-keep
                                            rounded-xl text-base font-semibold
                                            leading-[139.9999976158142%]`,
                                            position === $position
                                                ? `bg-primary-main font-bold
                                                    text-common-white-main`
                                                : `bg-grey-200 font-normal
                                                    text-text-secondary`
                                        )}
                                        onClick={() => {
                                            setPosition($position);
                                        }}
                                    >
                                        {POSITION_KOREAN[$position]}
                                    </button>
                                ))}
                            </div>
                        ),
                        [Stage.career]: (
                            <div
                                className="flex w-full flex-col gap-2 md:grid
                                    md:grid-cols-[repeat(3,minmax(0,1fr))]
                                    md:gap-1.5"
                            >
                                {ORDERED_CAREERS.map(($career) => (
                                    <button
                                        key={$career}
                                        className={cn(
                                            `h-[54px] w-full break-keep
                                            rounded-xl text-base font-semibold
                                            leading-[139.9999976158142%]`,
                                            career === $career
                                                ? `bg-primary-main font-bold
                                                    text-common-white-main`
                                                : `bg-grey-200 font-normal
                                                    text-text-secondary`
                                        )}
                                        onClick={() => {
                                            setCareer($career);
                                        }}
                                    >
                                        {$career}
                                    </button>
                                ))}
                            </div>
                        ),
                        [Stage.selectInterestTag]: (
                            <div className="flex flex-col items-start">
                                {me.group_id === "shinhaninvest" && (
                                    <>
                                        <div
                                            className="mb-4 flex items-center
                                                gap-2"
                                        >
                                            <Image
                                                src="/img/logo/shinhaninvest.png"
                                                alt="shinhaninvest"
                                                width={24}
                                                height={24}
                                            />
                                            <Text
                                                variant="button.small"
                                                color="text.primary"
                                            >
                                                신한투자증권 전용 카테고리
                                            </Text>
                                        </div>
                                        <div
                                            className="mb-2 flex cursor-pointer
                                                flex-wrap items-center
                                                justify-start gap-0.5"
                                            onClick={() =>
                                                toggleAllTags(
                                                    SHINHAN_INVEST_TAG_LIST
                                                )
                                            }
                                        >
                                            <Checkbox
                                                size="medium"
                                                checked={isAllTagsSelected(
                                                    SHINHAN_INVEST_TAG_LIST
                                                )}
                                            />
                                            <span className="text-primary-main">
                                                모든 카테고리
                                            </span>
                                        </div>
                                        <div
                                            className="mb-10 flex max-w-[483px]
                                                flex-wrap justify-start gap-3"
                                        >
                                            {(
                                                SHINHAN_INVEST_TAG_LIST as Mix_Content_Tag_Enum_Enum[]
                                            ).map((tag) => (
                                                <Button
                                                    key={tag}
                                                    variant="filled"
                                                    color={
                                                        selectedTags.has(tag)
                                                            ? "primary"
                                                            : "grey"
                                                    }
                                                    size="medium"
                                                    onClick={() =>
                                                        toggleTag(tag)
                                                    }
                                                    className={cn(
                                                        `h-[27px] rounded-xl
                                                        px-3.5 py-[14px]
                                                        pb-[14px] text-xs
                                                        font-semibold
                                                        leading-[139.9999976158142%]
                                                        transition-all
                                                        duration-200`,
                                                        "hover:border-primary-main",
                                                        !selectedTags.has(
                                                            tag
                                                        ) &&
                                                            "hover:bg-primary-hover",
                                                        "md:px-[10px] md:py-4"
                                                    )}
                                                >
                                                    {
                                                        MIX_CONTENT_TAG_ENUM_KOREAN[
                                                            tag
                                                        ]
                                                    }
                                                </Button>
                                            ))}
                                        </div>
                                        <Divider className="mb-10" />
                                        <div
                                            className="mb-4 flex items-center
                                                gap-2"
                                        >
                                            <Image
                                                src="/img/logo/mix.png"
                                                alt="mix "
                                                width={24}
                                                height={24}
                                            />
                                            <Text
                                                variant="button.small"
                                                color="text.primary"
                                            >
                                                믹스 기본 카테고리
                                            </Text>
                                        </div>
                                    </>
                                )}
                                <div
                                    className="mb-2 flex cursor-pointer
                                        flex-wrap items-center justify-start
                                        gap-0.5"
                                    onClick={() => toggleAllTags(defaultTags)}
                                >
                                    <Checkbox
                                        size="medium"
                                        checked={isAllTagsSelected(defaultTags)}
                                    />
                                    <span className="text-primary-main">
                                        모든 카테고리
                                    </span>
                                </div>
                                <div
                                    className="mb-10 flex max-w-[483px]
                                        flex-wrap justify-start gap-3"
                                >
                                    {defaultTags.map((tag) => (
                                        <Button
                                            key={tag}
                                            variant="filled"
                                            color={
                                                selectedTags.has(tag)
                                                    ? "primary"
                                                    : "grey"
                                            }
                                            size="medium"
                                            onClick={() => toggleTag(tag)}
                                            className={cn(
                                                `h-[27px] rounded-xl px-3.5
                                                py-[14px] pb-[14px] text-xs
                                                font-semibold
                                                leading-[139.9999976158142%]
                                                transition-all duration-200`,
                                                "hover:border-primary-main",
                                                !selectedTags.has(tag) &&
                                                    "hover:bg-primary-hover",
                                                "md:px-[10px] md:py-4"
                                            )}
                                        >
                                            {MIX_CONTENT_TAG_ENUM_KOREAN[tag]}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        ),
                        [Stage.subscribeEmail]: (
                            <div className="flex max-w-[342px] flex-col">
                                <Text
                                    className="mb-10 whitespace-pre-wrap
                                        text-text-disabled"
                                    variant="body3"
                                >
                                    {`오리지널 콘텐츠와 인기 콘텐츠를\n메일로 보내드려요.`}
                                </Text>
                                <div className="flex gap-2 [&_button]:w-[140px]">
                                    <Button
                                        size="xl"
                                        variant="filled"
                                        color="grey"
                                        onClick={() => {
                                            setSubscribeEmail(false);

                                            if (shouldShowExtensionModal) {
                                                setStage(
                                                    Stage.installExtension
                                                );
                                            } else {
                                                updateMe({
                                                    variables: {
                                                        id: me.id,
                                                        _set: {
                                                            position,
                                                            career,
                                                            subscribeEmail: false
                                                        }
                                                    }
                                                });
                                            }
                                        }}
                                    >
                                        다음에 할게요
                                    </Button>
                                    <Button
                                        size="xl"
                                        onClick={() => {
                                            setSubscribeEmail(true);

                                            if (shouldShowExtensionModal) {
                                                setStage(
                                                    Stage.installExtension
                                                );
                                            } else {
                                                updateMe({
                                                    variables: {
                                                        id: me.id,
                                                        _set: {
                                                            position,
                                                            career,
                                                            subscribeEmail: true
                                                        }
                                                    }
                                                });
                                            }
                                        }}
                                    >
                                        받아볼래요
                                    </Button>
                                </div>
                            </div>
                        ),
                        [Stage.installExtension]: (
                            <div className="flex flex-col">
                                <Text
                                    className="mb-10 whitespace-pre-wrap
                                        text-text-disabled"
                                    variant="body3"
                                >
                                    지금 바로 크롬/웨일 스토어에서 만나보세요.
                                </Text>
                                <div className="flex gap-2 [&_button]:w-[140px]">
                                    <Button
                                        size="xl"
                                        variant="filled"
                                        color="grey"
                                        onClick={() => {
                                            // Complete the signup process regardless of shouldShowExtensionModal
                                            updateMe({
                                                variables: {
                                                    id: me.id,
                                                    _set: {
                                                        position,
                                                        career,
                                                        subscribeEmail
                                                    }
                                                }
                                            });
                                        }}
                                    >
                                        다음에 할게요
                                    </Button>
                                    <Button
                                        size="xl"
                                        onClick={() => {
                                            window.open(
                                                getExtensionUrl(),
                                                "_blank"
                                            );
                                            // Complete the signup process regardless of shouldShowExtensionModal
                                            updateMe({
                                                variables: {
                                                    id: me.id,
                                                    _set: {
                                                        position,
                                                        career,
                                                        subscribeEmail
                                                    }
                                                }
                                            });
                                        }}
                                    >
                                        구경해 볼게요
                                    </Button>
                                </div>
                            </div>
                        )
                    })}
                    <div
                        className={cn(
                            "mt-6 flex h-8 w-full items-center justify-between",
                            "md:mt-4",
                            (stage === Stage.subscribeEmail ||
                                stage === Stage.installExtension) &&
                                "mt-0 h-full md:mt-0"
                        )}
                    >
                        <div>
                            {select({
                                [Stage.position]: <></>,
                                [Stage.career]: (
                                    <Button
                                        variant="background"
                                        color="grey"
                                        size="medium"
                                        startIcon="ChevronLeft"
                                        onClick={() => setStage(Stage.position)}
                                    >
                                        이전
                                    </Button>
                                ),
                                [Stage.selectInterestTag]: (
                                    <Button
                                        variant="background"
                                        color="grey"
                                        size="medium"
                                        startIcon="ChevronLeft"
                                        onClick={() => setStage(Stage.career)}
                                    >
                                        이전
                                    </Button>
                                ),
                                [Stage.subscribeEmail]: <></>,
                                [Stage.installExtension]: <></>
                            })}
                        </div>
                        <div
                            className="flex items-center justify-end gap-4
                                text-sm font-normal leading-[139.9999976158142%]
                                text-text-placeholder"
                        >
                            {select({
                                [Stage.position]: (
                                    <>
                                        {/* <span>1/3</span> */}
                                        <Button
                                            variant="filled"
                                            color="secondary"
                                            size="medium"
                                            disabled={!position}
                                            onClick={() =>
                                                setStage(Stage.career)
                                            }
                                        >
                                            다음
                                        </Button>
                                    </>
                                ),
                                [Stage.career]: (
                                    <>
                                        {/* <span>2/3</span> */}
                                        <Button
                                            variant="filled"
                                            color="secondary"
                                            size="medium"
                                            disabled={!career}
                                            onClick={() =>
                                                setStage(
                                                    Stage.selectInterestTag
                                                )
                                            }
                                        >
                                            다음
                                        </Button>
                                    </>
                                ),
                                [Stage.selectInterestTag]: (
                                    <Tooltip
                                        placement="top"
                                        trigger="mouseover"
                                        open={showTooltip || undefined}
                                    >
                                        <Tooltip.Trigger asChild={false}>
                                            <Button
                                                variant="filled"
                                                color="secondary"
                                                className="px-4 py-2"
                                                disabled={isNoTagsSelected}
                                                onClick={() => {
                                                    const tagsToUpsert =
                                                        Array.from(
                                                            selectedTags
                                                        );
                                                    if (
                                                        tagsToUpsert.length > 0
                                                    ) {
                                                        upsertUserInterestTags({
                                                            variables: {
                                                                objects:
                                                                    tagsToUpsert.map(
                                                                        (
                                                                            tag,
                                                                            index
                                                                        ) => ({
                                                                            user_id:
                                                                                me.id,
                                                                            tag_value:
                                                                                tag,
                                                                            priority:
                                                                                index +
                                                                                1
                                                                        })
                                                                    )
                                                            }
                                                        });
                                                    }
                                                    setStage(
                                                        Stage.subscribeEmail
                                                    );
                                                }}
                                            >
                                                확인
                                            </Button>
                                        </Tooltip.Trigger>
                                        {isNoTagsSelected && (
                                            <Tooltip.Content
                                                className="z-[1000]
                                                    max-w-[260px] break-keep
                                                    rounded-md px-2.5 py-2
                                                    text-xs"
                                            >
                                                카테고리를 최소 1개 이상 선택해
                                                주세요
                                            </Tooltip.Content>
                                        )}
                                    </Tooltip>
                                ),
                                [Stage.subscribeEmail]: "",
                                [Stage.installExtension]: ""
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </MixModal>
    );
};
